import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { 
  FileWarning,
  ExternalLink,
  Copy,
  Tag,
  AlertTriangle,
  Clock,
  Link as LinkIcon
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/Badge';
import { useTeams } from '../../contexts/TeamsContext';
import { AuthContext } from '../../contexts/AuthContext';
import { getApiConfiguration } from '../../api/utils';
import { SecretsApi, DataLeak } from 'api-client';

const DataLeakDetail = () => {
  const { id } = useParams();
  const [leak, setLeak] = useState<DataLeak | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentTeam } = useTeams();
  const teamSlug = currentTeam?.slug || '';
  const { token } = useContext(AuthContext);

  useEffect(() => {
    const fetchLeak = async () => {
      try {
        if (id && currentTeam?.slug && token) {
          const secretsApi = new SecretsApi(getApiConfiguration(token));
          const response = await secretsApi.dataLeaksRetrieve({
            id,
            teamSlug,
          });
          setLeak(response);
          setError(null);
        }
      } catch (err) {
        setError('Failed to fetch data leak details');
        console.error('Error fetching data leak:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLeak();
  }, [id, teamSlug, token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-400">{error}</div>
      </div>
    );
  }

  if (!leak) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Data leak not found</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start space-x-4">
            <div className="p-3 rounded-lg bg-red-500/10">
              <FileWarning className="h-6 w-6 text-red-500" />
            </div>
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-2xl font-bold text-white">Data Leak Details</h1>
              </div>
              <div className="flex items-center space-x-4 mt-2">
                <Badge variant={leak.severity as any}>{leak.severity?.toUpperCase()}</Badge>
                <Badge variant={leak.status as any}>{leak.status?.toUpperCase()}</Badge>
                {/* <span className="text-sm text-gray-400">Leak ID: {leak.id}</span> */}
                <div className="flex items-center space-x-2 text-sm text-gray-400">
                  <Tag className="h-4 w-4" />
                  <span className="px-2 py-0.5 rounded-full bg-gray-800/50 text-gray-300">
                    {leak.leakType?.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {leak.sourceLink && (
              <Button 
                variant="outline"
                onClick={() => window.open(leak.sourceLink!, '_blank')}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                View Source
              </Button>
            )}
            <Button 
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(leak.snippet);
              }}
            >
              <Copy className="h-4 w-4 mr-2" />
              Copy Snippet
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 mt-6">
          <div className="space-y-1">
            <div className="text-sm text-gray-400">Category</div>
            <div className="text-lg font-medium text-white">{leak.category || 'N/A'}</div>
          </div>
          <div className="space-y-1">
            <div className="text-sm text-gray-400">Created</div>
            <div className="text-sm text-white">{new Date(leak.createdAt).toLocaleString()}</div>
          </div>
          <div className="space-y-1">
            <div className="text-sm text-gray-400">Last Updated</div>
            <div className="text-sm text-white">{new Date(leak.updatedAt).toLocaleString()}</div>
          </div>
          <div className="space-y-1">
            <div className="text-sm text-gray-400">Type</div>
            <div className="text-sm text-white">{leak.leakType}</div>
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-3 gap-6">
        {/* Main Content - 2 columns */}
        <div className="col-span-2 space-y-6">
          {/* Description */}
          <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <h2 className="text-lg font-semibold text-white mb-4">Description</h2>
            <p className="text-gray-300 whitespace-pre-wrap">{leak.description || 'No description available'}</p>
          </div>

          {/* Leak Details */}
          <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <h2 className="text-lg font-semibold text-white mb-4">Leak Details</h2>
            <div className="space-y-4">
              <div>
                <div className="text-sm text-gray-400 mb-2">Snippet</div>
                <div className="bg-gray-800/30 rounded-lg p-4 overflow-auto max-h-96">
                  <pre className="text-sm text-white font-mono whitespace-pre-wrap break-all">
                    {leak.snippet}
                  </pre>
                </div>
              </div>

              {leak.otherInfo && (
                <div>
                  <div className="text-sm text-gray-400 mb-2">Additional Information</div>
                  <div className="bg-gray-800/30 rounded-lg p-4 overflow-auto max-h-96">
                    <pre className="text-sm text-white font-mono whitespace-pre-wrap break-all">
                      {JSON.stringify(leak.otherInfo, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {leak.sourceLink && (
                <div>
                  <div className="text-sm text-gray-400 mb-2">Source</div>
                  <a
                    href={leak.sourceLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 text-sm text-blue-500 hover:text-blue-400"
                  >
                    <LinkIcon className="h-4 w-4" />
                    <span>{leak.sourceLink}</span>
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar - 1 column */}
        <div className="space-y-6">
          {/* Status Information */}
          <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <h2 className="text-lg font-semibold text-white mb-4">Status Information</h2>
            <div className="space-y-4">
              <div>
                <div className="text-sm text-gray-400">Severity</div>
                <Badge variant={leak.severity as any} className="mt-1">
                  {leak.severity?.toLocaleUpperCase()}
                </Badge>
              </div>
              <div>
                <div className="text-sm text-gray-400">Status</div>
                <Badge variant={leak.status as any} className="mt-1">
                  {leak.status?.toLocaleUpperCase()}
                </Badge>
              </div>
              <div>
                <div className="text-sm text-gray-400">Category</div>
                <div className="text-sm text-white mt-1">{leak.category || 'N/A'}</div>
              </div>
            </div>
          </div>

          {/* Timestamps */}
          <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <h2 className="text-lg font-semibold text-white mb-4">Timestamps</h2>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <AlertTriangle className="h-4 w-4 text-gray-400" />
                <div>
                  <div className="text-sm text-gray-400">Created</div>
                  <div className="text-sm text-white">{new Date(leak.createdAt).toLocaleString()}</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Clock className="h-4 w-4 text-gray-400" />
                <div>
                  <div className="text-sm text-gray-400">Last Updated</div>
                  <div className="text-sm text-white">{new Date(leak.updatedAt).toLocaleString()}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataLeakDetail;
